import {ApplicationRef, Injectable} from "@angular/core";
import {
  DEFAULT_POLYGON_COLOR, DEFAULT_POLYGON_OPACITY,
  DISTANCE_LAYER_ID,
  DRAWING_SOURCE_ID, LINE_LABEL_FEATURE_TYPE, LINE_LABEL_LAYER,
  MARKERS_LAYER_ID,
  POLYGON_INFO_FEATURE_TYPE,
  POLYGON_INFO_LAYER, POLYGON_LABEL_FEATURE_TYPE, POLYGON_LABEL_LAYER,
} from "./consts/map.constants";
import * as mapboxgl from "mapbox-gl";
import {Store} from "@ngrx/store";
import {DataCentersService} from "./service/data-centers.service";
import {DistanceUnits, UserMap} from "./model/user-map";
import * as turf from "@turf/turf";
import {Feature} from "@turf/turf";
import {getCenterPoint} from "./ngrx/reducers/util/map/polygon-drawing.util";
import {
  polygonActions
} from 'src/app/core/ngrx/actions';
import {buildMapPolygonDataFromModel, buildMapPolygonModelFromFeature} from "./ngrx/reducers/util/map/map.util";
import {MapPolygon} from "./model/summary";
import {getMarkersInsidePolygon} from "./util/transform.util";
import * as _ from "lodash";
import {MapConfigSelector} from "./ngrx/selectors/map-config.selector";
import {filter, take, tap} from "rxjs";
import {Datacenter} from "./model/datacenter";
import {DatacenterV2} from "./model/datacenter-v2";

@Injectable()
export class PolygonsHandler {
  constructor(private readonly store: Store, private datacentersService: DataCentersService,
              private appRef: ApplicationRef, private mapConfigSelector: MapConfigSelector) {
  }

  public updateLineLabelsForNewUnits(map: mapboxgl.Map, units: DistanceUnits) {
    const geoJsonSource = map.getSource(DRAWING_SOURCE_ID) as any;
    if (geoJsonSource && geoJsonSource._data) {
      const featureCollection = geoJsonSource._data;

      // Update line labels with new units
      for (let i = 0; i < featureCollection.features.length; i++) {
        const feature = featureCollection.features[i];
        if (feature.geometry && feature.geometry.type === "LineString" &&
            feature.properties && feature.properties.featureType === LINE_LABEL_FEATURE_TYPE) {
          // Recreate the line feature with new units
          const updatedFeature = this.createDistanceLabelFeature(feature, units);
          featureCollection.features[i] = updatedFeature;
        }
      }

      geoJsonSource.setData(featureCollection);
    }
  }

  public addPolygonsSourceData(userMap: UserMap, map: mapboxgl.Map, polygonControl) {
    let geoJsonSource = map.getSource(DRAWING_SOURCE_ID) as any;
    let featureCollection: GeoJSON.FeatureCollection = null;
    if (!geoJsonSource) {
      featureCollection = {
        type: 'FeatureCollection',
        features: [],
      };
      map.addSource(DRAWING_SOURCE_ID, {
        type: 'geojson',
        data: featureCollection,
      });
      this.addPolygonInfoLayer(map);
      this.addLineDistanceLayer(map);
      this.addPolygonLabelLayer(map);
      geoJsonSource = map.getSource(DRAWING_SOURCE_ID) as mapboxgl.GeoJSONSource;
      if (userMap.polygons) {
        const fc = JSON.parse(userMap.polygons);
        // Get current distance units first
        this.mapConfigSelector.getDistanceUnits().pipe(
          take(1),
          tap((units: DistanceUnits) => {
            for (const feature of fc.features) {
              if (feature.geometry.type == "LineString") {
                const lineFeature = this.createDistanceLabelFeature(feature, units);
                featureCollection.features.push(lineFeature as any);
              } else {
                const pointFeature = this.createPointFeatureForPolygonLabel(feature);
                featureCollection.features.push(pointFeature as any);

                //create infosquare
                const infoFeature = this.createPolygonInfoFeature(map, feature);
                infoFeature.properties['label'] = this.fillInfoPolygonSquareLabel(feature);
                featureCollection.features.push(infoFeature as any);
              }
            }
            geoJsonSource.setData(featureCollection);
          })
        ).subscribe();
      } else {
        geoJsonSource.setData(featureCollection);
      }
    } else {
      geoJsonSource.setData(featureCollection);
    }
  }

  public onDrawCreate(map, event, polygonControl) {
    const [source, featureCollections] = this.datacentersService.getFeatureCollectionFromSource(map, DRAWING_SOURCE_ID);
    const feature = event.features[0];
    if (feature.geometry.type == "LineString") {
      // Get current distance units and create the label feature
      this.mapConfigSelector.getDistanceUnits().pipe(
        take(1),
        tap((units: DistanceUnits) => {
          const lineFeature = this.createDistanceLabelFeature(feature, units);
          featureCollections.features.push(lineFeature);
          source.setData(featureCollections);
        })
      ).subscribe();
    } else if (event.features[0].geometry.type == "Polygon") {
      this.handleDrawControlState();
      const pointFeature = this.createPointFeatureForPolygonLabel(feature);
      featureCollections.features.push(pointFeature);

      const mapPolygonData = buildMapPolygonModelFromFeature(feature);
      const polygonFeatureWithProperties = this.createPolygonFeatureWithProperties(feature);
      //creating info square
      const infoFeature = this.createPolygonInfoFeature(map, polygonFeatureWithProperties);
      const sumData = this.createSumForPolygonInfoFeature(map, polygonFeatureWithProperties);
      const sumLabel = this.createLabelPolygonInfoFeature(sumData);
      infoFeature.properties['label'] = sumLabel;
      featureCollections.features.push(infoFeature);

      source.setData(featureCollections);

      let existingFeatureCollection = polygonControl.getAll();
      existingFeatureCollection.features = existingFeatureCollection.features.filter(f => f.id != feature.id);
      existingFeatureCollection.features.push(polygonFeatureWithProperties);
      polygonControl.set(existingFeatureCollection);
      const mapPolygon: MapPolygon = {
        ...mapPolygonData[0],
        center: mapPolygonData[1],
        datacentersInside: [],
        isModifying: true,
        isReadOnly: false
      };
      setTimeout(() => {
        this.store.dispatch(
          polygonActions.polygonSelected({
            polygon: mapPolygon
          })
        );
      }, 100);
    }
  }

  public onPolygonModified(map, polygon: MapPolygon, polygonControl) {
    const mapPolygonData = this.createPolygonFeatureFromModel(polygon);
    let existingFeatureCollection = polygonControl.getAll();
    const originalFeatureFromControl = existingFeatureCollection.features.find(f => f.id == polygon.auxId);
    mapPolygonData.properties = {
      ...originalFeatureFromControl.properties,
      ...mapPolygonData.properties,
    }

    existingFeatureCollection.features = existingFeatureCollection.features.filter(f => f.properties['id'] != polygon.auxId);
    existingFeatureCollection.features.push(mapPolygonData);
    polygonControl.set(existingFeatureCollection);
    polygonControl.changeMode('simple_select');
    const [source, featureCollections] = this.datacentersService.getFeatureCollectionFromSource(map, DRAWING_SOURCE_ID);
    const pointFeature = featureCollections.features.find(f => f.properties['polygonId'] == polygon.auxId && f.properties['featureType'] === POLYGON_LABEL_FEATURE_TYPE);
    if (pointFeature) {
      pointFeature.properties['label'] = polygon.label;
      source.setData(featureCollections);
    }
  }

  public modifyPolygonSumOnDatacenterChange(map: mapboxgl.Map, polygonControl) {
    let polygonsFC = polygonControl.getAll();
    for (const feature of polygonsFC.features) {
      this.onPolygonShapeModified(map, {features: [feature]}, polygonControl);
    }
  }

  public onPolygonRightClick(event, polygonControl) {
    const mapPolygonData = buildMapPolygonModelFromFeature(event.features[0]);
    const mapPolygon: MapPolygon = {
      ...mapPolygonData[0],
      center: mapPolygonData[1],
      datacentersInside: [],
      isModifying: false,
      isReadOnly: false,
      mwLiveTotal: event.features[0].properties['user_sumMwLiveTotal'],
      mwUCTotal: event.features[0].properties['user_sumMwUcTotal'],
      mwPlannedTotal: event.features[0].properties['user_sumMwPlannedTotal']
    };
    this.store.dispatch(
      polygonActions.polygonSelected({
        polygon: mapPolygon
      })
    );
  }

  public onPolygonShapeModified(map: mapboxgl.Map, event, polygonControl) {
    const [source, featureCollections] = this.datacentersService.getFeatureCollectionFromSource(map, DRAWING_SOURCE_ID);
    const feature = event.features[0]
    if (feature.geometry.type == "LineString") {
      const lineFeature = this.createDistanceLabelFeature(feature);
      featureCollections.features = featureCollections.features.filter(f => f.id != feature.id);
      featureCollections.features.push(lineFeature);
      source.setData(featureCollections);
    } else {
      //creating info square
      const infoFeature = this.createPolygonInfoFeature(map, feature);
      featureCollections.features = featureCollections.features.filter(f => (f.properties['featureType'] != POLYGON_INFO_FEATURE_TYPE) || (f.properties['polygonId'] != feature.id && f.properties['featureType'] == POLYGON_INFO_FEATURE_TYPE));
      const sumData = this.createSumForPolygonInfoFeature(map, feature);
      const sumLabel = this.createLabelPolygonInfoFeature(sumData);
      infoFeature.properties['label'] = sumLabel;
      featureCollections.features.push(infoFeature);

      let existingFeatureCollection = polygonControl.getAll();
      existingFeatureCollection.features = existingFeatureCollection.features.filter(f => f.id != feature.id);
      existingFeatureCollection.features.push(feature);
      polygonControl.set(existingFeatureCollection);
      // updating polygon label position
      const pointFeature = this.createPointFeatureForPolygonLabel(feature);
      featureCollections.features = featureCollections.features.filter(f => (f.properties['featureType'] != POLYGON_LABEL_FEATURE_TYPE) || (f.properties['polygonId'] != feature.id && f.properties['featureType'] == POLYGON_LABEL_FEATURE_TYPE));
      if (pointFeature) {
        featureCollections.features.push(pointFeature);
      }
      source.setData(featureCollections);
    }
  }

  public onPolygonShapeDeleted(map: mapboxgl.Map, event) {
    const [source, featureCollections] = this.datacentersService.getFeatureCollectionFromSource(map, DRAWING_SOURCE_ID);
    const feature = event.features[0]
    if (feature.geometry.type == "LineString") {
      featureCollections.features = featureCollections.features.filter(f => f.id != feature.id);
      source.setData(featureCollections);
    } else {
      featureCollections.features = featureCollections.features.filter(f => f.properties['polygonId'] != feature.id);
      source.setData(featureCollections);
    }
  }

  private addPolygonInfoLayer(map: mapboxgl.Map) {
    map.addLayer({
      id: POLYGON_INFO_LAYER,
      type: 'symbol',
      source: DRAWING_SOURCE_ID,
      filter: ['==', 'featureType', POLYGON_INFO_FEATURE_TYPE],
      layout: {
        'symbol-placement': 'point',
        'text-max-width': 10,
        'text-field': ['get', 'label'],
        'text-size': [
          'interpolate',
          ['linear'],
          ['zoom'],
          0.1, 6,
          2, 6,
          2.5, 12,
          3, 12,
          15, 14
        ],
        'text-justify': 'left',
        'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
        'text-anchor': 'center',
      },
      paint: {
        'text-color': '#000000',
        'text-opacity': 1,
        'text-halo-color': '#FFFFFF',
        'text-halo-width': 100,
      },
    });
  }

  private addLineDistanceLayer(map: mapboxgl.Map) {
    map.addLayer({
      id: LINE_LABEL_LAYER,
      type: 'symbol',
      source: DRAWING_SOURCE_ID,
      filter: ['==', 'featureType', LINE_LABEL_FEATURE_TYPE],
      layout: {
        'text-field': ['get', 'label'],
        'text-size': [
          'interpolate',
          ['linear'],
          ['zoom'],
          0.1, 6,
          2, 6,
          2.5, 12,
          3, 12,
          15, 14
        ],
        'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
        'symbol-placement': 'line-center',
        'text-anchor': 'center',
        'text-offset': [0, 1.5],
        'text-max-width': 40,
        'text-allow-overlap': true,
        'text-ignore-placement': true
      },
      paint: {
        'text-color': '#038c33',
        'text-halo-color': '#FFFFFF',
        'text-halo-width': 3,
      },
    });
  }

  private addPolygonLabelLayer(map: mapboxgl.Map) {
    if (map.getLayer(POLYGON_LABEL_LAYER)) {
      map.removeLayer(POLYGON_LABEL_LAYER);
    }
    map.addLayer({
      id: POLYGON_LABEL_LAYER,
      type: 'symbol',
      source: DRAWING_SOURCE_ID,
      filter: ['==', ['get', 'featureType'], POLYGON_LABEL_FEATURE_TYPE],
      layout: {
        'text-field': ['get', 'label'],
        'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
        'text-anchor': 'center',
        'text-size': [
          'interpolate',
          ['linear'],
          ['zoom'],
          0.1, 4,
          2, 5,
          2.5, 8,
          3, 12,
          15, 12
        ],
        'text-allow-overlap': false,
        'text-ignore-placement': false
      },
      paint: {
        'text-color': '#007cbf',
        'text-opacity': [
          'interpolate',
          ['linear'],
          ['zoom'],
          1.8, 0,
          3, 1,
        ],
        'text-halo-width': 3,
        'text-halo-color': 'rgb(255,255,255)',
      },
    });
  }

  private numberWithCommas(x) {
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }

  private createDistanceLabelFeature(feature: Feature, units?: DistanceUnits) {
    var line = turf.lineString((feature.geometry as any).coordinates);
    const distanceKm = turf.length(line, {units: 'kilometers'});
    const latencyMs = 2 * (1 + (distanceKm / 200));
    const formattedLatency = latencyMs.toFixed(1);

    // Use provided units or default to kilometers for backward compatibility
    const distanceUnits = units || DistanceUnits.KMS;
    const distance = turf.length(line, {units: distanceUnits});
    const unitLabel = distanceUnits === DistanceUnits.MILES ? 'miles' : 'km';

    const distancelabel = `${this.numberWithCommas(distance.toFixed(0))} ${unitLabel}\n Latency (RTT) ≈ ${formattedLatency} ms`;

    const lineFeature: Feature = {
      id: feature.id,
      type: "Feature",
      geometry: {
        type: "LineString",
        coordinates: (feature.geometry as any).coordinates
      },
      properties: {
        id: feature.id,
        label: distancelabel,
        featureType: LINE_LABEL_FEATURE_TYPE
      }
    };
    return lineFeature;
  }

  private handleDrawControlState() {
    let mapboxButtons = document.getElementsByClassName("mapbox-gl-draw_ctrl-draw-btn");
    Array.prototype.filter.call(
      mapboxButtons,
      (mapboxButton) => {
        let circleButtons = mapboxButton.getElementsByClassName("circle");
        if (circleButtons.length) {
          if (mapboxButton.classList.contains("active")) {
            mapboxButton.classList.remove('active');
          }
        }
      },
    );
  }

  private createPointFeatureForPolygonLabel(feature: Feature) {
    const centerComplex = getCenterPoint(feature)
    const center = [centerComplex.geometry.coordinates[0], centerComplex.geometry.coordinates[1]];

    const pointFeature: Feature = {
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: center
      },
      properties: {
        polygonId: feature.id,
        label: feature.properties['label'] ? feature.properties['label'] : '',
        featureType: POLYGON_LABEL_FEATURE_TYPE
      }
    };
    return pointFeature
  }


  private createPolygonFeatureWithProperties(feature: Feature) {
    const polygonFeature: Feature = {
      id: feature.id,
      type: "Feature",
      geometry: {
        type: "Polygon",
        coordinates: (feature.geometry as any).coordinates
      },
      properties: {
        id: feature.id,
        hideDatacenters: feature.properties['hideDatacenters'] ? feature.properties['hideDatacenters'] : '',
        label: feature.properties['label'] ? feature.properties['label'] : '',
        color: feature.properties['color'] ? feature.properties['color'] : DEFAULT_POLYGON_COLOR,
        circleRadius: feature.properties['circleRadius'] ? feature.properties['circleRadius'] : -1,
        opacity: feature.properties['opacity'] ? feature.properties['opacity'] : DEFAULT_POLYGON_OPACITY,
        user_opacity: feature.properties['opacity'] ? feature.properties['opacity'] : DEFAULT_POLYGON_OPACITY,
      }
    };

    return polygonFeature;
  }

  private createPolygonFeatureFromModel(mapPolygon: MapPolygon): Feature {
    const polygonFeature: Feature = {
      id: mapPolygon.auxId,
      type: "Feature",
      geometry: {
        type: "Polygon",
        coordinates: mapPolygon.polygon
      },
      properties: {
        id: mapPolygon.auxId,
        hideDatacenters: mapPolygon.hideDatacenters,
        label: mapPolygon.label,
        color: mapPolygon.color ? mapPolygon.color : DEFAULT_POLYGON_COLOR,
        circleRadius: mapPolygon.circleRadius,
        opacity: mapPolygon.opacity || DEFAULT_POLYGON_OPACITY,
        user_opacity: mapPolygon.opacity || DEFAULT_POLYGON_OPACITY,
      }
    };
    return polygonFeature;
  }


  private createPolygonInfoFeature(map: mapboxgl.Map, polygonFeature: Feature) {
    let finalSquarePosition = null;
    let origPolygonSquare = null;
    let originalCenter = null;
    const circleRadius = polygonFeature.properties['circleRadius'] ? polygonFeature.properties['circleRadius'] : -1;
    if (circleRadius == -1) {
      let origPolygonBbox = turf.bbox(polygonFeature as any);
      origPolygonSquare = turf.bboxPolygon(origPolygonBbox);
      originalCenter = turf.center(turf.points([[origPolygonSquare.bbox[2], origPolygonSquare.bbox[3]],
        [origPolygonSquare.bbox[2], origPolygonSquare.bbox[1]]]));
    } else {
      originalCenter = turf.center(polygonFeature);
      let circleBuffer = turf.buffer(originalCenter, circleRadius, {steps: 0});
      let origPolygonBbox = turf.bbox(circleBuffer as any);
      origPolygonSquare = turf.bboxPolygon(origPolygonBbox);
      originalCenter = turf.center(turf.points([[origPolygonSquare.bbox[2], origPolygonSquare.bbox[3]],
        [origPolygonSquare.bbox[2], origPolygonSquare.bbox[1]]]));
    }

    let finalBuffer = turf.buffer(originalCenter, 10, {steps: 0});
    let finalBbox = turf.bbox(finalBuffer as any);
    let finalSquare = turf.bboxPolygon(finalBbox as any);

    finalSquarePosition = turf.transformTranslate(finalSquare, 6, 90);


    const squareInfoCenterComplex = getCenterPoint(finalSquarePosition)
    const squareInfoCenter = [squareInfoCenterComplex.geometry.coordinates[0], squareInfoCenterComplex.geometry.coordinates[1]];


    const squareInfoTextFeature: Feature = {
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: squareInfoCenter
      },
      properties: {
        polygonId: polygonFeature.properties['id'],
        featureType: POLYGON_INFO_FEATURE_TYPE
      }
    };

    return squareInfoTextFeature;
  }

  createSumForPolygonInfoFeature(map: mapboxgl.Map, feature: Feature) {
    const dcFeatures = this.getDatacentersInsidePolygon(map, feature);
    feature.properties['sumMwLiveTotal'] = _.sum(dcFeatures.features.map(dc => dc.properties['mwLive'] | 0));
    feature.properties['sumMwUcTotal'] = _.sum(dcFeatures.features.map(dc => dc.properties['mwUC'] | 0));
    feature.properties['sumMwPlannedTotal'] = _.sum(dcFeatures.features.map(dc => dc.properties['mwPlanned'] | 0));

    return {
      mwLiveTotal: feature.properties['sumMwLiveTotal'],
      mwUCTotal: feature.properties['sumMwUcTotal'],
      mwPlannedTotal: feature.properties['sumMwPlannedTotal']
    };
  }

  fillInfoPolygonSquareLabel(feature: Feature) {
    const label = `Live: ${feature.properties['sumMwLiveTotal']}\n` +
      `U/C: ${feature.properties['sumMwUcTotal']}\n` +
      `Plan: ${feature.properties['sumMwPlannedTotal']}`;
    return label
  }

  createLabelPolygonInfoFeature(sumData) {
    return `Live: ${sumData.mwLiveTotal}\n` +
      `U/C: ${sumData.mwUCTotal}\n` +
      `Plan: ${sumData.mwPlannedTotal}\n`;
  }

  getDatacentersInsidePolygon(map: mapboxgl.Map, polygon) {
    const featuresInView = map.queryRenderedFeatures(null, {
      layers: [MARKERS_LAYER_ID] // Specify the layer(s) associated with your GeoJSON source
    });
    const uniqueFeaturesInView = [];
    for (const feature of featuresInView) {
      if (!feature.properties['auxId']) {
        if (!uniqueFeaturesInView.find(f => f.properties['id'] == feature.properties ['id'])) {
          uniqueFeaturesInView.push(feature);
        }
      } else {
        if (!uniqueFeaturesInView.find(f => f.properties['auxId'] == feature.properties ['auxId'])) {
          uniqueFeaturesInView.push(feature);
        }
      }
    }

    let originalPolygon = polygon;
    if (originalPolygon.properties && originalPolygon.properties["circleRadius"] && originalPolygon.properties["circleRadius"] != -1) {
      let center = originalPolygon.geometry.coordinates[0][0];
      let radius = originalPolygon.properties["circleRadius"];
      var options = {units: "kilometers" as any};
      originalPolygon = turf.circle(center, radius, options);
    }
    let markersInside = turf.pointsWithinPolygon({
      type: "FeatureCollection",
      features: uniqueFeaturesInView
    } as any, originalPolygon);

    return markersInside;
  }

  public togglePolygonsVisibility(map: mapboxgl.Map, isVisible: boolean): void {
    if (!map) return;

    const layers = map.getStyle().layers;
    const drawLayers = layers.filter(layer =>
      (layer.id.startsWith('gl-draw-polygon') ||
       layer.id.includes('polygon')) &&
      !layer.id.includes(POLYGON_INFO_LAYER) &&
      !layer.id.includes(POLYGON_INFO_FEATURE_TYPE)
    );

    drawLayers.forEach(layer => {
      if (map.getLayer(layer.id)) {
        map.setLayoutProperty(layer.id, 'visibility', isVisible ? 'visible' : 'none');
      }
    });
  }

  public toggleInfoVisibility(map: mapboxgl.Map, isVisible: boolean): void {
    if (!map) return;

    const layers = map.getStyle().layers;
    const infoLayers = layers.filter(layer =>
      layer.id.includes(POLYGON_INFO_LAYER) ||
      layer.id.includes(POLYGON_INFO_FEATURE_TYPE)
    );

    infoLayers.forEach(layer => {
      if (map.getLayer(layer.id)) {
        map.setLayoutProperty(layer.id, 'visibility', isVisible ? 'visible' : 'none');
      }
    });
  }

  public toggleLinesVisibility(map: mapboxgl.Map, isVisible: boolean): void {
    if (!map) return;

    const layers = map.getStyle().layers;
    const lineLayers = layers.filter(layer =>
      layer.id.startsWith('gl-draw-line') ||
      layer.id.includes(LINE_LABEL_LAYER) ||
      layer.id.includes(LINE_LABEL_FEATURE_TYPE) ||
      layer.id.includes(DISTANCE_LAYER_ID)
    );

    lineLayers.forEach(layer => {
      if (map.getLayer(layer.id)) {
        map.setLayoutProperty(layer.id, 'visibility', isVisible ? 'visible' : 'none');
      }
    });
  }
}
